/**
 * Workforce Store — Staff, time entries, kiosk state
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { KetenStand } from '../lib/contract-keten';
import type { VakantieSaldo } from '../lib/vakantie-saldo';
import type { Beoordeling } from '../lib/verlof-beoordeling-types';

// Types
export interface WerkroosterDay {
  day: 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0=zo, 1=ma, 2=di, 3=wo, 4=do, 5=vr, 6=za
  from: string; // HH:mm
  to: string;   // HH:mm
}

export interface Staff {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  contract_type: 'vast' | 'min-max' | '0-uren' | 'student' | 'zzp' | 'dga' | 'jaaruren' | 'vast-salaris';
  uren_per_week: number | null;
  maand_norm: number;
  vakantiedagen_wettelijk: number | null;
  vakantiedagen_bovenwettelijk: number | null;
  uurloon: number | null;
  start_date: string | null;
  end_date: string | null;
  sf_medewerker_id: string | null;
  pin_code: string | null;
  is_active: boolean;
  team: 'wasserij' | 'route' | 'kantoor' | 'algemeen' | null;
  datum_in_dienst: string | null;
  fte_percentage: number | null;
  loongroep: number | null;
  geboortedatum: string | null;
  loon_type: 'bruto' | 'all-in';
  uurloon_handmatig: boolean;
  // Bruto maandsalaris bij vast-salaris (volgt het contract via loonkaart-sync), anders null
  maandsalaris: number | null;
  cao_ervaringsjaren: number | null;
  werkrooster: WerkroosterDay[] | null;
  reiskostenvergoeding_per_dag: number;
  aanhef: string | null;
  voorletters: string | null;
  woonplaats: string | null;
  adres: string | null;
  postcode: string | null;
  stad: string | null;
  bsn: string | null;
  bankrekening: string | null;
  noodnummer: string | null;
  functie: string | null;
  geboorteplaats: string | null;
  nationaliteit: string | null;
  noodcontact_naam: string | null;
  onboarding_status: 'invited' | 'completed' | 'active';
  onboarding_token: string | null;
  onboarding_token_expires: string | null;
  onboarded_at: string | null;
  onboarding_seen_at: string | null;
  created_at: string;
  updated_at: string;
}

export type LeaveType = 'vakantie' | 'ziekte' | 'bijzonder' | 'onbetaald' | 'feestdag' | 'verjaardag' | 'geboorteverlof' | 'aanvullend_geboorteverlof' | 'ouderschapsverlof';

export interface LeaveEntry {
  id: string;
  staff_id: string;
  date: string;
  leave_type: LeaveType;
  hours: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  staff?: { name: string; team: string | null };
}

export interface LeaveEntitlement {
  id: string;
  staff_id: string;
  leave_type: LeaveType;
  total_hours: number;
  description: string | null;
  valid_from: string;
  valid_until: string;
  created_at: string;
}

export interface LeaveRequest {
  id: string;
  staff_id: string;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  days: number;
  total_hours: number | null;
  /** pending → approved/rejected (kantoor) of withdrawn (medewerker); approved → cancelled (kantoor) */
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'cancelled';
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  withdrawn_at?: string | null;
  cancelled_by?: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  notes: string | null;
  created_at: string;
  staff?: { name: string; team: string | null; email: string | null };
}

export interface VakantieSaldoAntwoord {
  staff: { id: string; name: string; team: string | null; contract_type: string | null; email: string | null };
  saldo: VakantieSaldo;
}

export type BreakSource = 'schedule' | 'team_fallback' | 'manual' | 'clocked';

export interface TimeEntry {
  id: string;
  staff_id: string;
  date: string;
  clock_in: string;
  clock_out: string | null;
  break_start: string | null;
  break_end: string | null;
  break_minutes: number;
  paid_break_minutes: number | null;
  break_source: BreakSource | null;
  total_hours: number | null;
  entry_type: 'regular' | 'ziekte';
  notes: string | null;
  created_at: string;
  staff?: { name: string; team: string | null };
}

// Contracts
export interface Contract {
  id: string;
  contract_number: string;
  staff_id: string;
  contract_type: 'bepaald' | 'onbepaald';
  employment_type: 'regulier' | '0-uren-allin' | '0-uren-opbouw' | 'min-max' | 'jaaruren' | 'vast-salaris';
  status: 'concept' | 'werkgever_getekend' | 'verstuurd' | 'getekend' | 'verlopen' | 'beëindigd';
  functie: string;
  start_date: string;
  end_date: string | null;
  proeftijd_maanden: number;
  uren_per_week: number | null;
  uren_min: number | null;
  uren_max: number | null;
  jaaruren_norm: number | null;
  cao_dienstjaren: number | null;
  loongroep: number | null; // CAO-loongroep waarmee bruto_uurloon is berekend (migratie 220)
  bruto_uurloon: number;
  // Vast-salaris-contract: bruto maandsalaris (leidend); bruto_uurloon is dan de afgeleide waarde
  bruto_maandsalaris: number | null;
  vakantie_percentage: number;
  vakantietoeslag_percentage: number;
  vakantie_opbouw_per_week: number | null;
  vakantieuren_per_jaar: number | null;
  aanhef_werknemer: string;
  voorletters: string;
  achternaam: string;
  geboortedatum: string;
  woonplaats: string;
  bedrijf_vertegenwoordigd_door: string;
  datum_opgemaakt: string;
  // Contractwissel zittende werknemer: oorspronkelijke datum indiensttreding (anciënniteit); leeg = gewone indiensttreding
  oorspronkelijke_datum_in_dienst: string | null;
  employer_signed_at: string | null;
  employer_signature_data: string | null;
  employer_signed_by: string | null;
  signed_at: string | null;
  signed_ip: string | null;
  signature_data: string | null;
  signed_pdf_path: string | null;
  sent_at: string | null;
  sent_to_email: string | null;
  previous_contract_id: string | null;
  // Voortzetting van rechtswege (art. 7:668 lid 4): getekend zonder handtekening, alleen via contract-einde (migratie 269)
  stilzwijgend_verlengd?: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

// Contracteinde als beslispunt (api/workforce/contract-einde.ts)
export type ContractEindeKeuze = 'verlengen_zelfde' | 'verlengen_nieuw' | 'niet_verlengen' | 'stilzwijgend_vastleggen';

export interface ContractBeslispunt {
  id: string;
  fase: 't60' | 't35' | 'verlopen';
  einddatum: string;
  dagen: number;
  aanzegplicht: boolean;
  aanzegUiterlijk: string;
  gemeld_op: string | null;
  modus: string;
  staff: { id: string; name: string; email: string | null };
  contract: {
    id: string;
    nummer: string;
    employment_type: Contract['employment_type'];
    contract_type: Contract['contract_type'];
    functie: string | null;
    uren_per_week: number | null;
    uren_min: number | null;
    uren_max: number | null;
    bruto_uurloon: number | string | null;
    bruto_maandsalaris: number | string | null;
    start_date: string;
    end_date: string;
    stilzwijgend_verlengd: boolean;
  };
  keten: KetenStand | null;
  voorstel: { start_date: string; end_date: string | null; contract_type: 'bepaald' | 'onbepaald' } | null;
  toegestaneKeuzes: Array<{ keuze: ContractEindeKeuze; label: string; contract_type?: string | null; onbepaaldVerplicht?: boolean }>;
}

export interface ContractEindeResultaat {
  ok: boolean;
  error?: string;
  code?: string;
  nieuwContract?: Contract | null;
  mailOnderdrukt?: boolean;
}

export type ContractFormData = Omit<Contract, 'id' | 'contract_number' | 'status' | 'employer_signed_at' | 'employer_signature_data' | 'employer_signed_by' | 'signed_at' | 'signed_ip' | 'signature_data' | 'signed_pdf_path' | 'sent_at' | 'sent_to_email' | 'created_at' | 'updated_at'>;
// Loonkaart-terugschrijving: het contractformulier kiest loongroep + erkende dienstjaren; die staan
// op het contract en de API/cron loonkaart-sync zet ze op staff zodra het contract ingaat.
export type ContractCreateData = ContractFormData & {
  uurloon_via_cao?: boolean;
};

export interface SickLeave {
  id: string;
  staff_id: string;
  start_date: string;
  end_date: string | null;
  notes: string | null;
  created_at: string;
}

export interface OnBehalfResult {
  dry_run?: boolean;
  success?: boolean;
  request_id?: string;
  days: { date: string; hours: number }[];
  total_hours: number;
}

export interface GenerateEntitlementsProposal {
  staff_id: string;
  name: string;
  dagen: number;
  uren_per_week: number;
  pro_rata: number;
  total_hours: number;
  valid_from: string;
  valid_until: string;
  skipped?: string;
}

export interface GenerateEntitlementsResult {
  dry_run?: boolean;
  success?: boolean;
  year: number;
  created?: number;
  to_create?: number;
  proposals: GenerateEntitlementsProposal[];
}

export type KioskStatus = 'in' | 'out' | 'break';

export interface KioskStaffState {
  staff: Staff;
  status: KioskStatus;
  clockInTime: string | null;
  breakStartTime: string | null;
}

interface WorkforceState {
  // Staff
  staffList: Staff[];
  isLoadingStaff: boolean;
  // Of inactieve (uit dienst) medewerkers worden meegehaald. Uitgenodigde
  // medewerkers komen altijd mee zodra dit aanstaat. Persistent zodat de
  // polling-refresh de lijst niet terugzet naar alleen-actief.
  includeInactive: boolean;

  // Time entries
  todayEntries: TimeEntry[];
  timeEntries: TimeEntry[];
  isLoadingEntries: boolean;

  // Leave
  leaveEntries: LeaveEntry[];
  leaveEntitlements: LeaveEntitlement[];
  leaveRequests: LeaveRequest[];
  pendingLeaveCount: number;
  isLoadingLeave: boolean;
  /** beoordeling per aanvraag-id (cache; leeg na approve/reject/cancel) */
  verlofBeoordelingen: Record<string, Beoordeling>;

  // Kiosk
  kioskStaffStates: KioskStaffState[];
  selectedStaffId: string | null;
  isProcessing: boolean;

  // Sick leave
  sickLeaves: SickLeave[];

  // Contracts
  contracts: Contract[];
  isLoadingContracts: boolean;
  beslispunten: ContractBeslispunt[];
  uitDienstTeVerwerken: Array<{ id: string; name: string; end_date: string }>;
  contractEindeModus: 'uit' | 'schaduw' | 'live' | null;

  // Last error from a mutation action (for UI to inspect e.g. 423 period-lock)
  lastMutationError: { status?: number; body?: Record<string, unknown>; message?: string } | null;

  // Polling
  pollingIntervalId: ReturnType<typeof setInterval> | null;

  // Actions — Staff
  fetchStaff: (includeInactive?: boolean) => Promise<void>;
  createStaff: (staff: Partial<Staff>) => Promise<Staff | null>;
  updateStaff: (id: string, updates: Partial<Staff>) => Promise<Staff | null>;
  deleteStaff: (id: string) => Promise<boolean>;

  // Actions — Time entries
  fetchTodayEntries: () => Promise<void>;
  fetchTimeEntries: (params: { staff_id?: string; from?: string; to?: string }) => Promise<void>;
  updateTimeEntry: (id: string, updates: { clock_in?: string; clock_out?: string; break_minutes?: number; notes?: string }) => Promise<TimeEntry | null>;
  deleteTimeEntry: (id: string) => Promise<boolean>;
  createTimeEntry: (entry: { staff_id: string; date: string; clock_in: string; clock_out: string; break_minutes: number; notes?: string }) => Promise<TimeEntry | null>;

  // Actions — Leave
  fetchLeave: (params: { staff_id?: string; from?: string; to?: string }) => Promise<void>;
  createLeave: (entry: { staff_id: string; date: string; leave_type: LeaveType; hours?: number; notes?: string }) => Promise<LeaveEntry | null>;
  updateLeave: (id: string, updates: Partial<LeaveEntry>) => Promise<LeaveEntry | null>;
  deleteLeave: (id: string) => Promise<boolean>;

  // Actions — Leave Entitlements
  fetchEntitlements: (staffId: string) => Promise<void>;
  createEntitlement: (data: Omit<LeaveEntitlement, 'id' | 'created_at'>) => Promise<LeaveEntitlement | null>;
  deleteEntitlement: (id: string) => Promise<boolean>;

  // Actions — Leave Requests
  fetchLeaveRequests: (params?: { status?: string; staff_id?: string }) => Promise<void>;
  approveLeaveRequest: (id: string) => Promise<boolean>;
  rejectLeaveRequest: (id: string, reason: string) => Promise<boolean>;
  /** Goedgekeurd verlof annuleren (kantoor): entries weg, status cancelled, mail met reden */
  cancelLeaveRequest: (id: string, reason: string) => Promise<boolean>;
  /** Saldo vóór/na, overlap, collega's, rooster, feestdagen voor de goedkeurkaart */
  fetchVerlofBeoordeling: (id: string, opts?: { force?: boolean }) => Promise<Beoordeling | null>;
  /** Vakantiesaldo in lagen (wettelijk/bovenwettelijk met vervaldatum) */
  fetchVakantieSaldo: (staffId: string, peildatum?: string) => Promise<VakantieSaldoAntwoord | null>;
  fetchPendingCount: () => Promise<void>;
  createLeaveOnBehalf: (data: {
    staff_id: string;
    leave_type: LeaveType;
    start_date: string;
    end_date: string;
    notes?: string;
    dry_run?: boolean;
  }) => Promise<OnBehalfResult | null>;
  generateEntitlements: (year: number, dryRun: boolean) => Promise<GenerateEntitlementsResult | null>;

  // Actions — Sick leave
  fetchSickLeave: (staffId?: string) => Promise<void>;
  reportSick: (staffId: string, startDate: string, notes?: string) => Promise<SickLeave | null>;
  reportRecovered: (sickLeaveId: string, endDate: string) => Promise<SickLeave | null>;

  // Actions — Contracts
  fetchContracts: (staffId: string) => Promise<void>;
  createContract: (data: ContractCreateData) => Promise<{ contract: Contract; warning?: string } | null>;
  updateContract: (id: string, data: Partial<Contract>) => Promise<Contract | null>;
  deleteContract: (id: string) => Promise<boolean>;
  signContractAsEmployer: (id: string, signatureData: string, signedBy: string) => Promise<Contract | null>;
  sendContract: (id: string) => Promise<string>; // '' on success, error message on failure
  downloadContractPdf: (id: string) => Promise<void>;
  fetchBeslispunten: (staffId?: string) => Promise<void>;
  besluitContractEinde: (id: string, keuze: ContractEindeKeuze, overrides?: Record<string, unknown>) => Promise<ContractEindeResultaat>;

  // Actions — Kiosk
  clockIn: (staffId: string, pin: string) => Promise<boolean>;
  clockOut: (staffId: string, pin: string) => Promise<boolean>;
  startBreak: (staffId: string, pin: string) => Promise<boolean>;
  endBreak: (staffId: string, pin: string) => Promise<boolean>;
  setSelectedStaff: (id: string | null) => void;

  // Polling
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
}

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export async function apiFetch(url: string, options: RequestInit = {}) {
  const token = await getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    // Kiosk mode: attach device token
    const deviceToken = localStorage.getItem('kiosk_device_token');
    if (deviceToken) {
      headers['X-Device-Token'] = deviceToken;
    }
  }

  const response = await fetch(url, { ...options, headers: { ...headers, ...options.headers as Record<string, string> } });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    const error = new Error(err.message || err.error || 'Request failed') as Error & { status?: number; body?: Record<string, unknown> };
    error.status = response.status;
    error.body = err;
    throw error;
  }
  return response.json();
}

function deriveKioskState(staff: Staff[], entries: TimeEntry[]): KioskStaffState[] {
  return staff.filter(s => s.is_active).map(s => {
    const entry = entries.find(e => e.staff_id === s.id && !e.clock_out);
    let status: KioskStatus = 'out';
    let clockInTime: string | null = null;
    let breakStartTime: string | null = null;

    if (entry) {
      clockInTime = entry.clock_in;
      if (entry.break_start && !entry.break_end) {
        status = 'break';
        breakStartTime = entry.break_start;
      } else {
        status = 'in';
      }
    }

    return { staff: s, status, clockInTime, breakStartTime };
  });
}

export const useWorkforceStore = create<WorkforceState>()((set, get) => ({
  staffList: [],
  includeInactive: false,
  isLoadingStaff: false,
  todayEntries: [],
  timeEntries: [],
  isLoadingEntries: false,
  leaveEntries: [],
  leaveEntitlements: [],
  leaveRequests: [],
  verlofBeoordelingen: {},
  pendingLeaveCount: 0,
  isLoadingLeave: false,
  sickLeaves: [],
  contracts: [],
  isLoadingContracts: false,
  beslispunten: [],
  uitDienstTeVerwerken: [],
  contractEindeModus: null,
  kioskStaffStates: [],
  selectedStaffId: null,
  isProcessing: false,
  lastMutationError: null,
  pollingIntervalId: null,

  fetchStaff: async (includeInactive) => {
    set({ isLoadingStaff: true });
    // Onthoud de keuze zodat volgende (polling-)fetches 'm respecteren.
    if (includeInactive !== undefined) set({ includeInactive });
    try {
      const token = await getToken();
      const withInactive = get().includeInactive;
      const url = token
        ? `/api/workforce/staff${withInactive ? '?active=false' : ''}`
        : '/api/workforce/staff?mode=kiosk';
      const data = await apiFetch(url);
      set({ staffList: data, isLoadingStaff: false });
      // Re-derive kiosk states
      const { todayEntries } = get();
      set({ kioskStaffStates: deriveKioskState(data, todayEntries) });
    } catch (error) {
      console.error('[Workforce] fetchStaff error:', error);
      set({ isLoadingStaff: false });
    }
  },

  createStaff: async (staff) => {
    try {
      const data = await apiFetch('/api/workforce/staff', {
        method: 'POST',
        body: JSON.stringify(staff),
      });
      await get().fetchStaff();
      return data;
    } catch (error) {
      console.error('[Workforce] createStaff error:', error);
      return null;
    }
  },

  updateStaff: async (id, updates) => {
    try {
      const data = await apiFetch(`/api/workforce/staff?id=${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      await get().fetchStaff();
      return data;
    } catch (error) {
      console.error('[Workforce] updateStaff error:', error);
      return null;
    }
  },

  deleteStaff: async (id) => {
    try {
      await apiFetch(`/api/workforce/staff?id=${id}`, { method: 'DELETE' });
      await get().fetchStaff();
      return true;
    } catch (error) {
      console.error('[Workforce] deleteStaff error:', error);
      return false;
    }
  },

  fetchTodayEntries: async () => {
    set({ isLoadingEntries: true });
    try {
      const token = await getToken();
      const today = new Date().toISOString().split('T')[0];
      const url = token
        ? `/api/workforce/time-entries?date=${today}`
        : `/api/workforce/time-entries?mode=kiosk`;
      const data = await apiFetch(url);
      set({ todayEntries: data, isLoadingEntries: false });
      // Re-derive kiosk states
      const { staffList } = get();
      set({ kioskStaffStates: deriveKioskState(staffList, data) });
    } catch (error) {
      console.error('[Workforce] fetchTodayEntries error:', error);
      set({ isLoadingEntries: false });
    }
  },

  fetchTimeEntries: async (params) => {
    set({ isLoadingEntries: true });
    try {
      const qs = new URLSearchParams();
      if (params.staff_id) qs.set('staff_id', params.staff_id);
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      const data = await apiFetch(`/api/workforce/time-entries?${qs.toString()}`);
      set({ timeEntries: data, isLoadingEntries: false });
    } catch (error) {
      console.error('[Workforce] fetchTimeEntries error:', error);
      set({ isLoadingEntries: false });
    }
  },

  updateTimeEntry: async (id, updates) => {
    set({ lastMutationError: null });
    try {
      const data = await apiFetch(`/api/workforce/time-entries?action=update&id=${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      const { timeEntries } = get();
      set({ timeEntries: timeEntries.map(e => e.id === id ? { ...e, ...data } : e) });
      return data;
    } catch (error) {
      console.error('[Workforce] updateTimeEntry error:', error);
      const e = error as Error & { status?: number; body?: Record<string, unknown> };
      set({ lastMutationError: { status: e.status, body: e.body, message: e.message } });
      return null;
    }
  },

  deleteTimeEntry: async (id) => {
    set({ lastMutationError: null });
    try {
      await apiFetch(`/api/workforce/time-entries?action=delete&id=${id}`, { method: 'DELETE' });
      const { timeEntries } = get();
      set({ timeEntries: timeEntries.filter(e => e.id !== id) });
      return true;
    } catch (error) {
      console.error('[Workforce] deleteTimeEntry error:', error);
      const e = error as Error & { status?: number; body?: Record<string, unknown> };
      set({ lastMutationError: { status: e.status, body: e.body, message: e.message } });
      return false;
    }
  },

  createTimeEntry: async (entry) => {
    set({ lastMutationError: null });
    try {
      const data = await apiFetch('/api/workforce/time-entries?action=admin-create', {
        method: 'POST',
        body: JSON.stringify(entry),
      });
      const { timeEntries } = get();
      set({ timeEntries: [data, ...timeEntries] });
      return data;
    } catch (error) {
      console.error('[Workforce] createTimeEntry error:', error);
      const e = error as Error & { status?: number; body?: Record<string, unknown> };
      set({ lastMutationError: { status: e.status, body: e.body, message: e.message } });
      return null;
    }
  },

  fetchLeave: async (params) => {
    set({ isLoadingLeave: true });
    try {
      const qs = new URLSearchParams();
      if (params.staff_id) qs.set('staff_id', params.staff_id);
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      const data = await apiFetch(`/api/workforce/leave?${qs.toString()}`);
      set({ leaveEntries: data, isLoadingLeave: false });
    } catch (error) {
      console.error('[Workforce] fetchLeave error:', error);
      set({ isLoadingLeave: false });
    }
  },

  createLeave: async (entry) => {
    set({ lastMutationError: null });
    try {
      const data = await apiFetch('/api/workforce/leave', {
        method: 'POST',
        body: JSON.stringify(entry),
      });
      // Refresh leave entries with current range if loaded
      const { leaveEntries } = get();
      set({ leaveEntries: [...leaveEntries, data] });
      return data;
    } catch (error) {
      console.error('[Workforce] createLeave error:', error);
      const e = error as Error & { status?: number; body?: Record<string, unknown> };
      set({ lastMutationError: { status: e.status, body: e.body, message: e.message } });
      return null;
    }
  },

  updateLeave: async (id, updates) => {
    set({ lastMutationError: null });
    try {
      const data = await apiFetch(`/api/workforce/leave?id=${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      const { leaveEntries } = get();
      set({ leaveEntries: leaveEntries.map(e => e.id === id ? data : e) });
      return data;
    } catch (error) {
      console.error('[Workforce] updateLeave error:', error);
      const e = error as Error & { status?: number; body?: Record<string, unknown> };
      set({ lastMutationError: { status: e.status, body: e.body, message: e.message } });
      return null;
    }
  },

  deleteLeave: async (id) => {
    set({ lastMutationError: null });
    try {
      await apiFetch(`/api/workforce/leave?id=${id}`, { method: 'DELETE' });
      const { leaveEntries } = get();
      set({ leaveEntries: leaveEntries.filter(e => e.id !== id) });
      return true;
    } catch (error) {
      console.error('[Workforce] deleteLeave error:', error);
      const e = error as Error & { status?: number; body?: Record<string, unknown> };
      set({ lastMutationError: { status: e.status, body: e.body, message: e.message } });
      return false;
    }
  },

  fetchEntitlements: async (staffId) => {
    try {
      const data = await apiFetch(`/api/workforce/leave-entitlements?staff_id=${staffId}`);
      set({ leaveEntitlements: data });
    } catch (error) {
      console.error('[Workforce] fetchEntitlements error:', error);
    }
  },

  createEntitlement: async (data) => {
    try {
      const result = await apiFetch('/api/workforce/leave-entitlements', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      const { leaveEntitlements } = get();
      set({ leaveEntitlements: [...leaveEntitlements, result] });
      return result;
    } catch (error) {
      console.error('[Workforce] createEntitlement error:', error);
      return null;
    }
  },

  deleteEntitlement: async (id) => {
    try {
      await apiFetch(`/api/workforce/leave-entitlements?id=${id}`, { method: 'DELETE' });
      const { leaveEntitlements } = get();
      set({ leaveEntitlements: leaveEntitlements.filter(e => e.id !== id) });
      return true;
    } catch (error) {
      console.error('[Workforce] deleteEntitlement error:', error);
      return false;
    }
  },

  // ── Leave Requests ─────────────────────────────────────────

  fetchLeaveRequests: async (params) => {
    try {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.staff_id) qs.set('staff_id', params.staff_id);
      const data = await apiFetch(`/api/workforce/leave-requests?${qs.toString()}`);
      set({ leaveRequests: data });
    } catch (error) {
      console.error('[Workforce] fetchLeaveRequests error:', error);
    }
  },

  approveLeaveRequest: async (id) => {
    try {
      await apiFetch(`/api/workforce/leave-requests?id=${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ action: 'approve' }),
      });
      set(state => { const { [id]: _weg, ...rest } = state.verlofBeoordelingen; return { verlofBeoordelingen: rest }; });
      await get().fetchLeaveRequests();
      await get().fetchPendingCount();
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      console.error('[Workforce] approveLeaveRequest error:', msg);
      throw new Error(msg);
    }
  },

  rejectLeaveRequest: async (id, reason) => {
    try {
      await apiFetch(`/api/workforce/leave-requests?id=${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ action: 'reject', rejection_reason: reason }),
      });
      set(state => { const { [id]: _weg, ...rest } = state.verlofBeoordelingen; return { verlofBeoordelingen: rest }; });
      await get().fetchLeaveRequests();
      await get().fetchPendingCount();
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      console.error('[Workforce] rejectLeaveRequest error:', msg);
      throw new Error(msg);
    }
  },

  cancelLeaveRequest: async (id, reason) => {
    try {
      await apiFetch(`/api/workforce/leave-requests?id=${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ action: 'cancel', cancel_reason: reason }),
      });
      set(state => { const { [id]: _weg, ...rest } = state.verlofBeoordelingen; return { verlofBeoordelingen: rest }; });
      await get().fetchLeaveRequests();
      return true;
    } catch (error) {
      const err = error as Error & { status?: number; body?: Record<string, unknown> };
      let msg = err.message || 'Onbekende fout';
      if (err.status === 409 && err.body?.error === 'NIET_GOEDGEKEURD') msg = 'Alleen goedgekeurd verlof kan worden geannuleerd.';
      if (err.status === 423) msg = String(err.body?.message || 'De loonperiode is al afgesloten.');
      console.error('[Workforce] cancelLeaveRequest error:', msg);
      throw new Error(msg);
    }
  },

  fetchVerlofBeoordeling: async (id, opts) => {
    const cached = get().verlofBeoordelingen[id];
    if (cached && !opts?.force) return cached;
    try {
      const data: Beoordeling = await apiFetch(`/api/workforce/leave-requests?action=beoordeling&id=${id}`);
      set(state => ({ verlofBeoordelingen: { ...state.verlofBeoordelingen, [id]: data } }));
      return data;
    } catch (error) {
      console.error('[Workforce] fetchVerlofBeoordeling error:', error);
      return null;
    }
  },

  fetchVakantieSaldo: async (staffId, peildatum) => {
    try {
      const qs = new URLSearchParams({ staff_id: staffId });
      if (peildatum) qs.set('peildatum', peildatum);
      return await apiFetch(`/api/workforce/vakantie-saldo?${qs.toString()}`);
    } catch (error) {
      console.error('[Workforce] fetchVakantieSaldo error:', error);
      return null;
    }
  },

  fetchPendingCount: async () => {
    try {
      const data = await apiFetch('/api/workforce/leave-requests?status=pending');
      set({ pendingLeaveCount: Array.isArray(data) ? data.length : 0 });
    } catch (error) {
      console.error('[Workforce] fetchPendingCount error:', error);
    }
  },

  createLeaveOnBehalf: async (data) => {
    set({ lastMutationError: null });
    try {
      const result = await apiFetch(`/api/workforce/leave-requests?action=create${data.dry_run ? '&dry_run=true' : ''}`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!data.dry_run) {
        await get().fetchLeaveRequests();
        await get().fetchPendingCount();
      }
      return result;
    } catch (error) {
      console.error('[Workforce] createLeaveOnBehalf error:', error);
      const e = error as Error & { status?: number; body?: Record<string, unknown> };
      set({ lastMutationError: { status: e.status, body: e.body, message: e.message } });
      return null;
    }
  },

  generateEntitlements: async (year, dryRun) => {
    try {
      return await apiFetch('/api/workforce/leave-entitlements?action=generate', {
        method: 'POST',
        body: JSON.stringify({ year, dry_run: dryRun }),
      });
    } catch (error) {
      console.error('[Workforce] generateEntitlements error:', error);
      return null;
    }
  },

  // ── Sick Leave ──────────────────────────────────────────

  fetchSickLeave: async (staffId) => {
    try {
      const url = staffId ? `/api/workforce/sick-leave?staff_id=${staffId}` : '/api/workforce/sick-leave';
      const data = await apiFetch(url);
      set({ sickLeaves: data });
    } catch (error) {
      console.error('[Workforce] fetchSickLeave error:', error);
    }
  },

  reportSick: async (staffId, startDate, notes) => {
    try {
      const data = await apiFetch('/api/workforce/sick-leave', {
        method: 'POST',
        body: JSON.stringify({ staff_id: staffId, start_date: startDate, notes }),
      });
      const { sickLeaves } = get();
      set({ sickLeaves: [data, ...sickLeaves] });
      return data;
    } catch (error) {
      console.error('[Workforce] reportSick error:', error);
      return null;
    }
  },

  reportRecovered: async (sickLeaveId, endDate) => {
    try {
      const data = await apiFetch(`/api/workforce/sick-leave?id=${sickLeaveId}`, {
        method: 'PATCH',
        body: JSON.stringify({ end_date: endDate }),
      });
      const { sickLeaves } = get();
      set({ sickLeaves: sickLeaves.map(sl => sl.id === sickLeaveId ? data : sl) });
      return data;
    } catch (error) {
      console.error('[Workforce] reportRecovered error:', error);
      return null;
    }
  },

  // ── Contracts ────────────────────────────────────────────

  fetchContracts: async (staffId) => {
    set({ isLoadingContracts: true });
    try {
      const data = await apiFetch(`/api/workforce/contracts?staff_id=${staffId}`);
      set({ contracts: data, isLoadingContracts: false });
    } catch (error) {
      console.error('[Workforce] fetchContracts error:', error);
      set({ isLoadingContracts: false });
    }
  },

  createContract: async (data) => {
    set({ lastMutationError: null });
    try {
      const result = await apiFetch('/api/workforce/contracts', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      await get().fetchContracts(data.staff_id);
      return result;
    } catch (error) {
      console.error('[Workforce] createContract error:', error);
      // 409 KETEN_ONBEPAALD: de pagina toont de reden
      const e = error as Error & { status?: number; body?: Record<string, unknown> };
      set({ lastMutationError: { status: e.status, body: e.body, message: e.message } });
      return null;
    }
  },

  updateContract: async (id, updates) => {
    try {
      const data = await apiFetch(`/api/workforce/contracts?id=${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      const { contracts } = get();
      set({ contracts: contracts.map(c => c.id === id ? data : c) });
      return data;
    } catch (error) {
      console.error('[Workforce] updateContract error:', error);
      return null;
    }
  },

  deleteContract: async (id) => {
    try {
      await apiFetch(`/api/workforce/contracts?id=${id}`, { method: 'DELETE' });
      const { contracts } = get();
      set({ contracts: contracts.filter(c => c.id !== id) });
      return true;
    } catch (error) {
      console.error('[Workforce] deleteContract error:', error);
      return false;
    }
  },

  signContractAsEmployer: async (id, signatureData, signedBy) => {
    try {
      const data = await apiFetch(`/api/workforce/contracts?id=${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          employer_signature_data: signatureData,
          employer_signed_by: signedBy,
        }),
      });
      const { contracts } = get();
      set({ contracts: contracts.map(c => c.id === id ? data : c) });
      return data;
    } catch (error) {
      console.error('[Workforce] signContractAsEmployer error:', error);
      return null;
    }
  },

  sendContract: async (id) => {
    try {
      await apiFetch('/api/workforce/contracts-send', {
        method: 'POST',
        body: JSON.stringify({ contract_id: id }),
      });
      const { contracts } = get();
      set({ contracts: contracts.map(c => c.id === id ? { ...c, status: 'verstuurd' as const } : c) });
      return '';
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Onbekende fout';
      console.error('[Workforce] sendContract error:', msg);
      return msg;
    }
  },

  fetchBeslispunten: async (staffId) => {
    try {
      const qs = staffId ? `?staff_id=${staffId}` : '';
      const data = await apiFetch(`/api/workforce/contract-einde${qs}`);
      set({
        beslispunten: data.beslispunten ?? [],
        contractEindeModus: data.modus ?? null,
        // De uit-dienst-lijst komt alleen mee zonder staff-filter
        ...(staffId ? {} : { uitDienstTeVerwerken: data.uitDienstTeVerwerken ?? [] }),
      });
    } catch (error) {
      console.error('[Workforce] fetchBeslispunten error:', error);
    }
  },

  besluitContractEinde: async (id, keuze, overrides) => {
    const staffId = get().beslispunten.find(b => b.id === id)?.staff.id;
    try {
      const data = await apiFetch('/api/workforce/contract-einde', {
        method: 'POST',
        body: JSON.stringify({ beslispunt_id: id, keuze, overrides }),
      });
      set({ beslispunten: get().beslispunten.filter(b => b.id !== id) });
      await get().fetchStaff(true);
      if (staffId) await get().fetchContracts(staffId);
      return { ok: true, nieuwContract: data.nieuwContract ?? null, mailOnderdrukt: !!data.mailOnderdrukt };
    } catch (error) {
      const e = error as Error & { body?: Record<string, unknown> };
      console.error('[Workforce] besluitContractEinde error:', e.message);
      return { ok: false, error: e.message, code: (e.body?.code as string | undefined) ?? undefined };
    }
  },

  downloadContractPdf: async (id) => {
    try {
      const token = await getToken();
      const response = await fetch(`/api/workforce/contracts-pdf?id=${id}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('PDF download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract-${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[Workforce] downloadContractPdf error:', error);
    }
  },

  clockIn: async (staffId, pin) => {
    set({ isProcessing: true });
    try {
      await apiFetch('/api/workforce/time-entries?action=clock-in', {
        method: 'POST',
        body: JSON.stringify({ staff_id: staffId, pin }),
      });
      await get().fetchTodayEntries();
      set({ isProcessing: false, selectedStaffId: null });
      return true;
    } catch (error) {
      console.error('[Workforce] clockIn error:', error);
      set({ isProcessing: false });
      return false;
    }
  },

  clockOut: async (staffId, pin) => {
    set({ isProcessing: true });
    try {
      await apiFetch('/api/workforce/time-entries?action=clock-out', {
        method: 'POST',
        body: JSON.stringify({ staff_id: staffId, pin }),
      });
      await get().fetchTodayEntries();
      set({ isProcessing: false, selectedStaffId: null });
      return true;
    } catch (error) {
      console.error('[Workforce] clockOut error:', error);
      set({ isProcessing: false });
      return false;
    }
  },

  startBreak: async (staffId, pin) => {
    set({ isProcessing: true });
    try {
      await apiFetch('/api/workforce/time-entries?action=break-start', {
        method: 'POST',
        body: JSON.stringify({ staff_id: staffId, pin }),
      });
      await get().fetchTodayEntries();
      set({ isProcessing: false, selectedStaffId: null });
      return true;
    } catch (error) {
      console.error('[Workforce] startBreak error:', error);
      set({ isProcessing: false });
      return false;
    }
  },

  endBreak: async (staffId, pin) => {
    set({ isProcessing: true });
    try {
      await apiFetch('/api/workforce/time-entries?action=break-end', {
        method: 'POST',
        body: JSON.stringify({ staff_id: staffId, pin }),
      });
      await get().fetchTodayEntries();
      set({ isProcessing: false, selectedStaffId: null });
      return true;
    } catch (error) {
      console.error('[Workforce] endBreak error:', error);
      set({ isProcessing: false });
      return false;
    }
  },

  setSelectedStaff: (id) => set({ selectedStaffId: id }),

  startPolling: (intervalMs = 30000) => {
    const { pollingIntervalId } = get();
    if (pollingIntervalId) return;

    // Initial fetch
    get().fetchStaff();
    get().fetchTodayEntries();

    let tick = 0;
    const id = setInterval(() => {
      get().fetchTodayEntries();
      tick++;
      // Refresh staff list every 4th tick (~2 min at default 30s interval)
      // so new employees appear without requiring a page reload
      if (tick % 4 === 0) {
        get().fetchStaff();
      }
    }, intervalMs);
    set({ pollingIntervalId: id });
  },

  stopPolling: () => {
    const { pollingIntervalId } = get();
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      set({ pollingIntervalId: null });
    }
  },
}));

// Real-time subscription for kiosk live updates
supabase
  .channel('time_entries_changes')
  .on('postgres_changes', { event: '*', schema: 'public', table: 'time_entries' }, () => {
    // Refresh today's entries when any change happens
    useWorkforceStore.getState().fetchTodayEntries();
  })
  .subscribe();
